/**
 * 권한별 메뉴를 구성한다.
 * @version 1.0.0
 * @created 2025-11-13
 * @updated 2026-02-08
 */

import { AUTHORITIES, type Authority, type UserType } from "@/constants/auth/authority";

// Type definitions
/** 메뉴 그룹 타입. */
export interface NavbarDropdown {
  url: string;
  title: string;
  ariaLabel: string;
  children: NavbarMenuItem[];
}

/** 메뉴 항목 타입. */
export interface NavbarMenuItem {
  key: string;
  url: string;
  title: string;
  allowedUserTypes?: UserType[];
}

/** 권한별 메뉴 설정. */
const NAVBAR_SCOPE_CONFIG: Partial<Record<Authority, NavbarDropdown>> = {
  RESERVATION: {
    title: "예약관리",
    url: "/reservation",
    ariaLabel: "CMS reservation",
    children: [
      { title: "일일 예약", key: "reservation-daily", url: "/reservation/daily", allowedUserTypes: ["ADMIN", "MANAGER", "COUNTER"] },
      { title: "예약 목록", key: "reservation-list", url: "/reservation/list", allowedUserTypes: ["ADMIN", "MANAGER", "COUNTER"] },
      { title: "타임 관리",  key: "reservation-slot", url: "/reservation/slot", allowedUserTypes: ["ADMIN", "MANAGER", "COUNTER"] },
      { title: "알림 관리",key: "reservation-notification", url: "/reservation/notification",allowedUserTypes: ["ADMIN", "MANAGER", "COUNTER"], },
      { title: "상품 관리",key: "reservation-product", url: "/reservation/product",allowedUserTypes: ["ADMIN", "MANAGER"], },
    ],
  },
  CUSTOMER: {
    title: "고객관리",
    url: "/customer",
    ariaLabel: "CMS customer",
    children: [
      { title: "고객 목록", key: "customers", url: "/customer/list", allowedUserTypes: ["ADMIN", "MANAGER", "COUNTER"] },
      { title: "동의서 목록", key: "agreement", url: "/customer/agreement", allowedUserTypes: ["ADMIN", "MANAGER", "COUNTER"] },
      { title: "동의서 목록(고객)", key: "agreement-staging", url: "/customer/agreement/staging", allowedUserTypes: ["ADMIN", "MANAGER", "COUNTER"] },
      { title: "수료증 목록",key: "certificate", url: "/customer/certificate", allowedUserTypes: ["ADMIN", "MANAGER", "COUNTER"] },
    ],
  },
  FINANCE: {
    title: "정산관리",
    url: "/payment",
    ariaLabel: "CMS payment",
    children: [
      { title: "현금", key: "fund-cash", url: "/payment/cash", allowedUserTypes: ["ADMIN", "MANAGER", "COUNTER"] },
      { title: "토스포스", key: "fund-card-tosspos", url: "/payment/tosspos", allowedUserTypes: ["ADMIN", "MANAGER"] },
      { title: "네이버페이", key: "fund-naver", url: "/payment/naver", allowedUserTypes: ["ADMIN", "MANAGER"] },
      { title: "신한은행", key: "fund-account-sh-1", url: "/payment/account/sh-1", allowedUserTypes: ["ADMIN"] },
      { title: "농협은행 (운영)", key: "fund-account-nh-1", url: "/payment/account/nh-1", allowedUserTypes: ["ADMIN",] },
      { title: "농협은행 (매출)", key: "fund-account-nh-2", url: "/payment/account/nh-2", allowedUserTypes: ["ADMIN", "MANAGER"] },
    ],
  },
  EMPLOYEE: {
    title: "직원관리",
    url: "/employee",
    ariaLabel: "CMS employee",
    children: [
      {
        title: "직원 목록",
        key: "employee",
        url: "/employee/list",
        allowedUserTypes: ["ADMIN", "MANAGER"],
      },
      {
        title: "근태 목록",
        key: "vacation-list",
        url: "/employee/attendance",
        allowedUserTypes: ["ADMIN", "MANAGER"],
      },
      {
        title: "근태 관리",
        key: "attendance-list",
        url: "/employee/attendance/management",
        allowedUserTypes: ["ADMIN", "MANAGER"],
      },
      {
        title: "출퇴근 목록",
        key: "attendance-log-list",
        url: "/employee/attendance/history",
        allowedUserTypes: ["ADMIN", "MANAGER", "COUNTER"],
      },
    ],
  },
  CONSOLE: {
    title: "시스템관리",
    url: "/console",
    ariaLabel: "CMS console",
    children: [
      { title: "허용 목록", key: "console-allowlist", url: "/console/allowlist", allowedUserTypes: ["ADMIN"], },
      { title: "지표 관리", key: "console-metrics", url: "/console/metrics", allowedUserTypes: ["ADMIN"], },
    ],
  },
  PROFILE: {
    title: "프로필",
    url: "/profile",
    ariaLabel: "CMS profile",
    children: [
      { title: "프로필", key: "profile", url: "/profile", allowedUserTypes: ["ADMIN", "MANAGER", "USER"] },
      { title: "출퇴근", key: "profile-attendance", url: "/profile/attendance", allowedUserTypes: ["ADMIN", "MANAGER", "USER"] },
    ],
  },
};

/**
 * 권한과 사용자 유형에 맞는 메뉴를 만든다.
 * @param page_scopes Allowed page scopes.
 * @param user_type User type.
 * @returns Visible menu groups.
 */
export function buildNarbarConfig(page_scopes: string[], user_type: UserType): NavbarDropdown[] {
  const menuItems: NavbarDropdown[] = [];

  // Preserve authority display order.
  for (const { key: scope } of AUTHORITIES) {
    const scopeConfig = NAVBAR_SCOPE_CONFIG[scope];

    if (page_scopes.includes(scope) && scopeConfig) {

      // Filter by user type.
      const filteredChildren = scopeConfig.children.filter((item) => {
        // Show unrestricted items.
        if (!item.allowedUserTypes) return true;

        // Check allowed user types.
        return item.allowedUserTypes.includes(user_type);
      });

      // Keep groups with visible items.
      if (filteredChildren.length > 0) {
        menuItems.push({
          ...scopeConfig,
          children: filteredChildren,
        });
      }
    }
  }

  return menuItems;
}
